import React, { useState } from 'react';
import CardListItem from './CardListItem';
import CardListItemElement from './CardListItemElement';
import Badge from './Badge';
import Button from './Button/Button';
import Dialog from './Dialog';
import Tooltip from './Tooltip';
import Lozenge from '@/dashboard/components/Lozenge';
import { upperFirst } from 'lodash';
import { formatDateTime } from '@/dashboard/lib/format-date';
import Dash from './Dash';

function OpenedStatus({ opened, openedAt }) {
  if (opened === true && openedAt) {
    return `${formatDateTime(openedAt)}`;
  }

  if (opened === true) {
    return <Lozenge variant="success">Opened</Lozenge>;
  } else if (opened === false) {
    return <Lozenge variant="info">Unopened</Lozenge>;
  } else {
    return <Dash />;
  }
}

function DeliveredStatus({ delivered, deliveredAt }) {
  if (delivered === true && deliveredAt) {
    return `${formatDateTime(deliveredAt)}`;
  }

  if (delivered === true) {
    return <Lozenge variant="success">Delivered</Lozenge>;
  } else if (delivered === false) {
    return <Lozenge variant="error">Pending</Lozenge>;
  } else {
    return <Dash />;
  }
}

function EmailRecipients({ recipients, layout = 'default' }) {
  const parsedRecipients =
    recipients.length === 1
      ? recipients[0].split(',').map(recipient => recipient.trim())
      : recipients;

  const isTableLayout = layout === 'table';

  if (parsedRecipients.length > 1) {
    return (
      <div
        className={`inline-flex items-center justify-start gap-2 ${isTableLayout ? 'w-full' : ''}`}
      >
        <span className="truncate" title={parsedRecipients[0]}>
          {parsedRecipients[0]}
        </span>

        <div tabIndex={0} className="flex-shrink-0">
          <Tooltip
            label={parsedRecipients.slice(1).map(recipient => (
              <div key={recipient}>{recipient}</div>
            ))}
          >
            <span className="bg-gray-200 rounded-md px-1 py-0.5 text-xs">
              +{parsedRecipients.length - 1}
            </span>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <span className="truncate" title={parsedRecipients[0]}>
      {parsedRecipients[0] || <Dash />}
    </span>
  );
}

const getDisplayStatusInfo = log => {
  // Use Postmark message event if available for highest specificity
  const event = log.postmarkMessageEvent;

  if (event === 'Delivered' || event === 'Opened') {
    return { text: 'Delivered', appearance: 'success' };
  }

  if (event === 'Bounced') {
    return { text: 'Bounced', appearance: 'error' };
  }

  if (event === 'Queued') {
    return { text: 'Processing', appearance: 'warning' };
  }

  // Fallback to internal status field
  switch (log.status) {
    case 'created':
      return { text: 'Created', appearance: 'info' };
    case 'pending':
      return { text: 'Processing', appearance: 'warning' };
    case 'succeeded':
      return { text: 'Sent', appearance: 'success' };
    case 'failed':
      return { text: 'Failed', appearance: 'error' };
    default:
      return { text: log.status || 'Unknown', appearance: 'info' };
  }
};

const EmailLogListItem = ({ log }) => {
  const [showEmailContent, setShowEmailContent] = useState(false);
  const emailBody = `data:text/html;charset=utf-8,${encodeURIComponent(log.body)}`;

  const { text: statusText, appearance: statusAppearance } = getDisplayStatusInfo(log);

  const formattedLogType = logType => {
    if (
      ['abandonment_reminder_1', 'abandonment_reminder_2', 'abandonment_reminder_3'].includes(
        logType,
      )
    ) {
      return upperFirst(logType.replace('abandonment_', '').replace('_', ' '));
    } else {
      return upperFirst(
        logType
          .replace('charge_', '')
          .replace('subscription_', '')
          .replace('submission_', '')
          .replace('payment_', ''),
      );
    }
  };

  return (
    <>
      <CardListItem>
        <CardListItemElement size="fill" className="!pl-0">
          <EmailRecipients recipients={log.recipients} layout="table" />
        </CardListItemElement>

        <CardListItemElement size="xl">
          <Lozenge variant="info">{formattedLogType(log.type)}</Lozenge>
        </CardListItemElement>

        <CardListItemElement size="large">
          <div className="mb-0.5">
            {log.status ? <Badge appearance={statusAppearance}>{statusText}</Badge> : <Dash />}
          </div>
        </CardListItemElement>

        <CardListItemElement size="xl">
          <DeliveredStatus delivered={log.delivered} deliveredAt={log.deliveredAt} />
        </CardListItemElement>

        <CardListItemElement size="xl">
          <OpenedStatus opened={log.opened} openedAt={log.openedAt} />
        </CardListItemElement>

        <CardListItemElement size="medium" align="right">
          {log.body && (
            <Button appearance="secondary" size="small" onClick={() => setShowEmailContent(true)}>
              View email
            </Button>
          )}
        </CardListItemElement>
      </CardListItem>

      <Dialog
        open={showEmailContent}
        onCancel={() => setShowEmailContent(false)}
        size="2xl"
        cancelButton={null}
        confirmButton={null}
      >
        <div className="mb-4">
          <div className="text-sm text-gray-600">Subject: {log.subject}</div>
          <div className="text-sm text-gray-600">From: {log.from}</div>
          <div className="text-sm text-gray-600">
            To: <EmailRecipients recipients={log.recipients} />
          </div>
          {log.attachments && log.attachments.length > 0 && (
            <div className="text-sm text-gray-600">
              Attachments:{' '}
              {log.attachments.map((attachment, i) => (
                <React.Fragment key={attachment.name}>
                  <a
                    href={`data:application/pdf;base64,${attachment.content}`}
                    download={attachment.name}
                    className="underline text-black"
                  >
                    {attachment.name}
                  </a>
                  {i < log.attachments.length - 1 && ', '}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="text-sm text-gray-600">As sent at: {formatDateTime(log.createdAt)}</div>
        </div>

        <div className="w-full h-[70vh] border border-gray-100 rounded-lg">
          <iframe className="w-full h-[70vh]" src={emailBody} />
        </div>
      </Dialog>
    </>
  );
};

export default EmailLogListItem;
